import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";
import {
  Stethoscope, Check, X as XIcon, Download, Clock, Search,
  Building2, Mail, Linkedin, FileText, Loader2, ChevronDown, ChevronUp,
} from "lucide-react";

const STATUS_CONFIG = {
  pending: { label: "Pending", labelBg: "Изчакващ", color: "bg-amber-100 text-amber-800 border-amber-200" },
  approved: { label: "Approved", labelBg: "Одобрен", color: "bg-green-100 text-green-800 border-green-200" },
  rejected: { label: "Rejected", labelBg: "Отхвърлен", color: "bg-red-100 text-red-800 border-red-200" },
};

export default function ClinicianVerification() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const fetchApplications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/clinician-applications`);
      setApplications(data.applications || []);
    } catch {
      toast.error(bg ? "Грешка при зареждане" : "Failed to load applications");
    } finally {
      setLoading(false);
    }
  }, [API, bg]);

  useEffect(() => { fetchApplications(); }, [fetchApplications]);

  const handleReview = async (userId, action) => {
    setReviewLoading(userId);
    try {
      await axios.post(`${API}/admin/clinician-applications/${userId}/review`, {
        action,
        reason: action === "reject" ? rejectReason : "",
      });
      toast.success(action === "approve"
        ? (bg ? "Лекарят е одобрен" : "Clinician approved")
        : (bg ? "Заявката е отхвърлена" : "Application rejected"));
      setRejectReason("");
      fetchApplications();
    } catch {
      toast.error(bg ? "Грешка при обработка" : "Failed to process review");
    } finally {
      setReviewLoading(null);
    }
  };

  const downloadDoc = (userId, docType) => {
    window.open(`${API}/admin/clinician-documents/${userId}/${docType}`, "_blank");
  };

  const filtered = applications.filter((a) => {
    if (filter !== "all" && a.clinician_status !== filter) return false;
    if (search && !a.name.toLowerCase().includes(search.toLowerCase()) && !a.email.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const pendingCount = applications.filter((a) => a.clinician_status === "pending").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#7C3AED]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="clinician-verification-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Stethoscope className="w-5 h-5 text-[#7C3AED]" />
          <h2 className={`text-base font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            {bg ? "Заявки от лекари" : "Clinician Applications"}
          </h2>
          {pendingCount > 0 && (
            <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs">
              {pendingCount} {bg ? "нови" : "pending"}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={bg ? "Търси..." : "Search..."} data-testid="clinician-search"
              className={`pl-8 h-9 text-sm w-48 rounded-lg ${isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`} />
          </div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="clinician-filter"
            className={`h-9 px-3 text-sm rounded-lg border ${isDark ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-200 text-[#1A1A2E]"}`}>
            <option value="all">{bg ? "Всички" : "All"}</option>
            <option value="pending">{bg ? "Изчакващи" : "Pending"}</option>
            <option value="approved">{bg ? "Одобрени" : "Approved"}</option>
            <option value="rejected">{bg ? "Отхвърлени" : "Rejected"}</option>
          </select>
        </div>
      </div>

      {/* Applications list */}
      {filtered.length === 0 ? (
        <Card className={isDark ? "bg-gray-900 border-gray-800" : ""}>
          <CardContent className="py-8 text-center">
            <Stethoscope className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className={`text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>
              {bg ? "Няма заявки" : "No applications found"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((app) => {
            const cp = app.clinician_profile || {};
            const docs = cp.documents || {};
            const statusCfg = STATUS_CONFIG[app.clinician_status] || STATUS_CONFIG.pending;
            const isExpanded = expandedId === app.id;
            const isReviewing = reviewLoading === app.id;

            return (
              <Card key={app.id} data-testid={`clinician-app-${app.id}`}
                className={`transition-all ${isDark ? "bg-gray-900 border-gray-800" : "border-gray-100"} ${
                  app.clinician_status === "pending" ? (isDark ? "border-l-4 border-l-amber-500" : "border-l-4 border-l-amber-400") : ""
                }`}>
                <CardContent className="p-4">
                  {/* Main row */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isDark ? "bg-[#7C3AED]/20" : "bg-[#7C3AED]/10"}`}>
                        <Stethoscope className="w-5 h-5 text-[#7C3AED]" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-semibold truncate ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                            {app.name}
                          </span>
                          <Badge className={`${statusCfg.color} text-[10px] px-1.5 py-0`}>
                            {bg ? statusCfg.labelBg : statusCfg.label}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>{app.email}</span>
                          <span>{cp.specialty || ""}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {app.clinician_status === "pending" && (
                        <>
                          <Button size="sm" onClick={() => handleReview(app.id, "approve")} disabled={isReviewing}
                            data-testid={`approve-clinician-${app.id}`}
                            className="h-8 px-3 bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg">
                            {isReviewing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                            {bg ? "Одобри" : "Approve"}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setExpandedId(isExpanded ? null : app.id)}
                            data-testid={`expand-clinician-${app.id}`}
                            className={`h-8 px-3 text-xs rounded-lg ${isDark ? "border-gray-700 text-gray-300" : ""}`}>
                            {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          </Button>
                        </>
                      )}
                      {app.clinician_status !== "pending" && (
                        <Button size="sm" variant="ghost" onClick={() => setExpandedId(isExpanded ? null : app.id)}
                          className="h-8 px-2">
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className={`mt-4 pt-3 border-t space-y-3 ${isDark ? "border-gray-800" : "border-gray-100"}`}>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                        {cp.institution && (
                          <div className="flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5 text-gray-400" />
                            <span className={isDark ? "text-gray-300" : "text-gray-600"}>{cp.institution}</span>
                          </div>
                        )}
                        {cp.institutional_email && (
                          <div className="flex items-center gap-1.5">
                            <Mail className="w-3.5 h-3.5 text-gray-400" />
                            <span className={isDark ? "text-gray-300" : "text-gray-600"}>{cp.institutional_email}</span>
                          </div>
                        )}
                        {cp.orcid && (
                          <div className="flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5 text-gray-400" />
                            <span className={isDark ? "text-gray-300" : "text-gray-600"}>ORCID: {cp.orcid}</span>
                          </div>
                        )}
                        {cp.linkedin && (
                          <div className="flex items-center gap-1.5">
                            <Linkedin className="w-3.5 h-3.5 text-gray-400" />
                            <a href={cp.linkedin.startsWith("http") ? cp.linkedin : `https://${cp.linkedin}`}
                              target="_blank" rel="noopener noreferrer"
                              className="text-[#0EA5E9] hover:underline truncate">{cp.linkedin}</a>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-gray-400" />
                          <span className={isDark ? "text-gray-300" : "text-gray-600"}>
                            {app.created_at ? new Date(app.created_at).toLocaleDateString() : "N/A"}
                          </span>
                        </div>
                      </div>

                      {/* Documents */}
                      {(docs.cv || docs.diploma) && (
                        <div className="flex gap-2">
                          {docs.cv && (
                            <Button size="sm" variant="outline" onClick={() => downloadDoc(app.id, "cv")}
                              data-testid={`download-cv-${app.id}`}
                              className={`h-8 text-xs rounded-lg ${isDark ? "border-gray-700" : ""}`}>
                              <Download className="w-3 h-3 mr-1" /> CV ({docs.cv.original_name})
                            </Button>
                          )}
                          {docs.diploma && (
                            <Button size="sm" variant="outline" onClick={() => downloadDoc(app.id, "diploma")}
                              data-testid={`download-diploma-${app.id}`}
                              className={`h-8 text-xs rounded-lg ${isDark ? "border-gray-700" : ""}`}>
                              <Download className="w-3 h-3 mr-1" /> {bg ? "Диплома" : "Diploma"} ({docs.diploma.original_name})
                            </Button>
                          )}
                        </div>
                      )}

                      {/* Reject action */}
                      {app.clinician_status === "pending" && (
                        <div className={`flex items-center gap-2 p-3 rounded-lg ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                          <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                            placeholder={bg ? "Причина за отхвърляне (по желание)" : "Rejection reason (optional)"}
                            data-testid={`reject-reason-${app.id}`}
                            className={`flex-1 h-8 text-xs rounded-lg ${isDark ? "bg-gray-900 border-gray-700" : ""}`} />
                          <Button size="sm" variant="destructive" onClick={() => handleReview(app.id, "reject")} disabled={isReviewing}
                            data-testid={`reject-clinician-${app.id}`}
                            className="h-8 px-3 text-xs rounded-lg">
                            {isReviewing ? <Loader2 className="w-3 h-3 animate-spin" /> : <XIcon className="w-3 h-3 mr-1" />}
                            {bg ? "Отхвърли" : "Reject"}
                          </Button>
                        </div>
                      )}

                      {/* Review info for already reviewed */}
                      {app.clinician_status !== "pending" && cp.reviewed_at && (
                        <div className={`text-xs p-2 rounded-lg ${isDark ? "bg-gray-800 text-gray-400" : "bg-gray-50 text-gray-500"}`}>
                          {bg ? "Прегледан от" : "Reviewed by"}: {cp.reviewed_by || "admin"} | {new Date(cp.reviewed_at).toLocaleString()}
                          {cp.rejection_reason && (
                            <span className="block mt-1 text-red-500">{bg ? "Причина" : "Reason"}: {cp.rejection_reason}</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
